import React, { useCallback, useEffect, useState } from 'react'
import { CBadge, CCard, CCardBody, CSmartTable } from '@coreui/react-pro'

import ToastComponent from 'src/components/common/TaostComponent.js'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import RowActionsMenu from 'src/components/common/RowActionsMenu'
import UserService from 'src/service/UserService'
import Notify from '../Notify'
import AssignMembershipPlan from './AssignMembershipPlan'
import './User.css'
import './ManageMembership.css'

// users/manageMembership only filters on these two.
const SEARCH_FIELDS = [
  { key: 'full_name', label: 'Member' },
  { key: 'email', label: 'Email' },
]

const columns = [
  { label: 'Member', key: 'full_name', filter: false },
  { label: 'Email', key: 'email', filter: false },
  { label: 'Account', key: 'user_role_id', filter: false },
  { label: 'Membership', key: 'membership_is_paid', filter: false },
  { label: 'Actions', key: 'action', filter: false, sorter: false, _style: { width: '1%' } },
]

const ManageMembership = () => {
  const [loading, setLoading] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [columnFilter, setColumnFilter] = useState({})
  const [searchField, setSearchField] = useState('full_name')
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [users, setUsers] = useState()

  const [payingMember, setPayingMember] = useState(null)
  const [unpayingMember, setUnpayingMember] = useState(null)

  const clearSearchKeys = (filter) => {
    const next = { ...filter }
    SEARCH_FIELDS.forEach((f) => delete next[f.key])
    return next
  }

  const applySearch = (value) => {
    setActivePage(1)
    const next = clearSearchKeys(columnFilter)
    if (value) next[searchField] = value
    setColumnFilter(next)
  }

  const handleFieldChange = (key) => {
    setSearchField(key)
    setColumnFilter((prev) => clearSearchKeys(prev))
  }

  const fetchData = useCallback(() => {
    const offset = itemsPerPage * activePage - itemsPerPage
    const params = new URLSearchParams()
    Object.keys(columnFilter).forEach((key) => {
      if (columnFilter[key]) params.append(key, columnFilter[key])
    })
    if (columnSorter && columnSorter.column !== undefined) {
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)
    }

    setLoading(true)
    UserService.membershipListing(offset, itemsPerPage, activePage, params)
      .then((result) => setUsers(result.data))
      .catch((e) => {
        ToastComponent(e?.response?.data?.message || 'Could not load members', 'error')
      })
      .finally(() => setLoading(false))
  }, [activePage, columnFilter, columnSorter, itemsPerPage])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const confirmMarkUnpaid = () => {
    const member = unpayingMember
    setUnpayingMember(null)
    setLoading(true)
    UserService.deleteMembershipByAdmin(member.id)
      .then((res) => {
        ToastComponent(res.message, 'success')
        fetchData()
      })
      .catch((e) => {
        setLoading(false)
        ToastComponent(e?.response?.data?.message || 'Could not remove membership', 'error')
      })
  }

  const activeSearchLabel = SEARCH_FIELDS.find((f) => f.key === searchField)?.label

  return (
    <>
      <div className="ge-page-header">
        <h1 className="ge-page-title">Manage Membership</h1>
        <p className="ge-page-subtitle">
          Mark members as paid for a game or an annual membership, or take a membership away.
        </p>
      </div>

      <CCard className="um-panel">
        <CCardBody>
          <div className="um-panel-heading">
            <div>
              <h2 className="um-panel-title">
                Members
                {users?.total !== undefined && <span className="ge-tab-count">{users.total}</span>}
              </h2>
              <p className="um-panel-note">Approved members and hub admins across all games.</p>
            </div>
          </div>

          <TableSearchToolbar
            fields={SEARCH_FIELDS}
            field={searchField}
            onFieldChange={handleFieldChange}
            value={columnFilter?.[searchField] || ''}
            onSearch={applySearch}
            placeholder={`Search by ${activeSearchLabel}...`}
          />

          <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
            <CSmartTable
              columns={columns}
              columnSorter={{ external: true }}
              noItemsLabel="No members found"
              scopedColumns={{
                full_name: (item) => <td className="fw-semibold">{item.full_name || '-'}</td>,
                email: (item) => <td>{item.email || '-'}</td>,
                user_role_id: (item) => (
                  <td>
                    <CBadge
                      color={item.user_role_id === 3 ? 'info' : 'secondary'}
                      shape="rounded-pill"
                      className="px-2 py-1"
                    >
                      {item.user_role_id === 3 ? 'Hub Admin' : 'Member'}
                    </CBadge>
                  </td>
                ),
                membership_is_paid: (item) => (
                  <td>
                    <CBadge
                      color={item.membership_is_paid ? 'success' : 'warning'}
                      shape="rounded-pill"
                      className="px-2 py-1"
                    >
                      {item.membership_is_paid ? 'Paid' : 'Unpaid'}
                    </CBadge>
                  </td>
                ),
                action: (item) => (
                  <td className="text-nowrap">
                    <RowActionsMenu
                      actions={[
                        item.membership_is_paid
                          ? {
                              label: 'Mark Unpaid',
                              color: 'danger',
                              danger: true,
                              onClick: () => setUnpayingMember(item),
                            }
                          : { label: 'Mark Paid', onClick: () => setPayingMember(item) },
                      ]}
                    />
                  </td>
                ),
              }}
              items={users?.data}
              itemsPerPage={itemsPerPage}
              itemsPerPageSelect
              loading={loading}
              pagination={{ external: true }}
              paginationProps={{
                activePage,
                pages: Math.ceil(users?.total / itemsPerPage) || 1,
              }}
              tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
              onActivePageChange={(page) => setActivePage(page)}
              onItemsPerPageChange={(perPage) => {
                setActivePage(1)
                setItemsPerPage(perPage)
              }}
              onSorterChange={(sorter) => setColumnSorter(sorter)}
            />
          </div>
        </CCardBody>
      </CCard>

      <AssignMembershipPlan
        member={payingMember}
        visible={!!payingMember}
        onClose={() => setPayingMember(null)}
        onSaved={fetchData}
      />

      <Notify
        showConfirm={!!unpayingMember}
        setShowConfirm={(show) => !show && setUnpayingMember(null)}
        handleConfirm={confirmMarkUnpaid}
        handleCancel={() => setUnpayingMember(null)}
        text={`Mark ${
          unpayingMember?.full_name || 'this member'
        } as unpaid? This removes all of their game and annual memberships.`}
      />
    </>
  )
}

export default ManageMembership
